import DOMPurify from "dompurify";
import { useMemo } from "react";

/**
 * Client-side sichere Ausgabe von Rich-Text-HTML aus dem Admin-CMS.
 *
 * Doppelte Absicherung: Backend sanitisiert schon vor DB-Write (nh3), hier
 * sanitisiert DOMPurify nochmal vor DOM-Insert — Defense in Depth gegen den
 * Fall, dass ältere DB-Einträge oder ein späterer Import-Weg ungefilterte
 * Werte einliefert.
 *
 * Whitelist streng: nur die Tags/Attribute, die der `RichTextField`-Editor
 * generiert. Kein `<script>`, keine On-Event-Handler, keine `javascript:`-URLs.
 *
 * Rückwärtskompatibilität: Wenn `html` KEIN HTML-Tag enthält, rendert die
 * Komponente den Plain-Text (mit `\n` → `<br>`). So funktionieren alte
 * Textarea-Werte weiterhin ohne Migration.
 */

const ALLOWED_TAGS = [
    "p", "br", "strong", "em", "u", "span", "a", "mark",
];
const ALLOWED_ATTR = [
    "href", "target", "rel", "style", "class",
];

function looksLikeHtml(s: string): boolean {
    return /<[a-z][\s\S]*>/i.test(s);
}

function plainToHtml(s: string): string {
    // \n → <br>, escape HTML-Sonderzeichen. Damit rendert alter Textarea-
    // Content ohne Verlust.
    const escaped = s
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
    // Doppelte Newlines → Absätze; einfache Newlines → weiche Breaks.
    return escaped
        .split(/\n{2,}/)
        .map((para) => `<p>${para.replace(/\n/g, "<br/>")}</p>`)
        .join("");
}

type Props = {
    /** Gespeicherter Wert — HTML (RichTextField) ODER Plain-Text (Legacy). */
    html?: string | null;
    className?: string;
    testId?: string;
    /** Wenn true: Absatz-Wrapper nicht wiederholt (nutze für inline-Kontexte
     *  wie Hero-Subline, wo äußerer Container schon einen Absatz-Style hat). */
    inline?: boolean;
    /** Ersatz-Element für leere Werte (default: null → nichts rendern). */
    fallback?: React.ReactNode;
};

export default function RichText({
    html,
    className,
    testId,
    inline = false,
    fallback = null,
}: Props) {
    const clean = useMemo(() => {
        if (!html || !html.trim()) return "";
        const source = looksLikeHtml(html) ? html : plainToHtml(html);
        return DOMPurify.sanitize(source, {
            ALLOWED_TAGS,
            ALLOWED_ATTR,
            // Nur http(s), mailto, tel – kein javascript:, data:, etc.
            ALLOWED_URI_REGEXP: /^(?:https?:|mailto:|tel:|#|\/)/i,
        }) as string;
    }, [html]);

    if (!clean) return <>{fallback}</>;

    if (inline) {
        // Entferne umschließenden <p>-Wrapper, wenn nur ein einzelner Absatz
        // vorliegt — sonst würde er einen Zeilenumbruch erzwingen.
        const stripped = clean.replace(/^\s*<p>([\s\S]*?)<\/p>\s*$/i, "$1");
        return (
            <span
                className={`hf-rt ${className || ""}`}
                data-testid={testId}
                dangerouslySetInnerHTML={{ __html: stripped }}
            />
        );
    }

    return (
        <div
            className={`hf-rt ${className || ""}`}
            data-testid={testId}
            dangerouslySetInnerHTML={{ __html: clean }}
        />
    );
}
